import React, { useEffect, useRef, useState, useCallback } from 'react'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

interface Props {
  token:string; balance:number|null; playerId?:string; operatorId?:string
}
type Phase = 'idle'|'batting'|'bowling'|'six'|'wicket'|'retired'

// ─── Crowd colours ────────────────────────────────────────────────────────────
const CROWD_COLS = [0xee4444,0x4466ee,0xffffff,0xeeee44,0x44ee88,0xee8844,0xaa44ee]

// ─── Build stadium ────────────────────────────────────────────────────────────
function buildStadium(scene:THREE.Scene){
  // Outfield
  const field = new THREE.Mesh(
    new THREE.CircleGeometry(38,64),
    new THREE.MeshStandardMaterial({color:0x2d8c3e,roughness:.9})
  )
  field.rotation.x=-Math.PI/2; field.position.y=-.01; scene.add(field)

  // Boundary circle (white rope)
  const rope = new THREE.Mesh(
    new THREE.TorusGeometry(32,.08,6,120),
    new THREE.MeshStandardMaterial({color:0xffffff,roughness:.6})
  )
  rope.rotation.x=Math.PI/2; rope.position.y=.02; scene.add(rope)

  // Pitch strip
  const pitchGeo = new THREE.PlaneGeometry(3,22)
  const pitchMat = new THREE.MeshStandardMaterial({color:0xc8a46e,roughness:.85})
  const pitchMesh = new THREE.Mesh(pitchGeo,pitchMat)
  pitchMesh.rotation.x=-Math.PI/2; pitchMesh.position.y=.01; scene.add(pitchMesh)

  // Crease lines
  const creaseMat = new THREE.MeshStandardMaterial({color:0xffffff,roughness:.5})
  ;[-8,8].forEach(z=>{
    const crease=new THREE.Mesh(new THREE.PlaneGeometry(3.6,.06),creaseMat)
    crease.rotation.x=-Math.PI/2; crease.position.set(0,.02,z); scene.add(crease)
  })

  // Stadium walls + seating tiers
  const wallMat = new THREE.MeshStandardMaterial({color:0x3a3a5c,roughness:.8})
  const wall = new THREE.Mesh(new THREE.CylinderGeometry(38,40,6,48,1,true),wallMat)
  wall.position.y=2; scene.add(wall)

  // Crowd (colorful boxes in rings)
  for(let ring=0;ring<7;ring++){
    const r=33+ring*1.2, n=Math.floor(r*1.8), h=0.5+ring*0.5
    for(let i=0;i<n;i++){
      const ang=(i/n)*Math.PI*2
      const col=CROWD_COLS[Math.floor(Math.random()*CROWD_COLS.length)]
      const crowd=new THREE.Mesh(
        new THREE.BoxGeometry(.5,.7,.3),
        new THREE.MeshStandardMaterial({color:col,roughness:.9})
      )
      crowd.position.set(Math.cos(ang)*r, h, Math.sin(ang)*r)
      crowd.rotation.y=ang+Math.PI
      scene.add(crowd)
    }
  }

  // Floodlight towers (4 corners)
  const towMat=new THREE.MeshStandardMaterial({color:0x888899,roughness:.6,metalness:.4})
  const lightMat=new THREE.MeshStandardMaterial({color:0xffffee,emissive:new THREE.Color(0xffffcc),emissiveIntensity:1.5})
  ;[[30,30],[-30,30],[30,-30],[-30,-30]].forEach(([x,z])=>{
    // Tower pole
    const pole=new THREE.Mesh(new THREE.CylinderGeometry(.2,.3,18,8),towMat)
    pole.position.set(x,8,z); scene.add(pole)
    // Light cluster
    for(let li=0;li<4;li++){
      const light=new THREE.Mesh(new THREE.BoxGeometry(.8,.4,.5),lightMat)
      light.position.set(x+(li%2-.5)*.8,17.5+Math.floor(li/2)*.5,z)
      scene.add(light)
    }
    // Actual point light
    const pl=new THREE.PointLight(0xfff8e0,1.5,60)
    pl.position.set(x,18,z); scene.add(pl)
  })
}

// ─── Build stumps ─────────────────────────────────────────────────────────────
function buildStumps(scene:THREE.Scene, z:number): THREE.Group {
  const grp  = new THREE.Group()
  const wood = new THREE.MeshStandardMaterial({color:0xd4a84b,roughness:.7})
  for(let i=-1;i<=1;i++){
    const s=new THREE.Mesh(new THREE.CylinderGeometry(.025,.025,.72,8),wood)
    s.position.set(i*.115,.36,0); grp.add(s)
  }
  ;[-.06,.06].forEach(x=>{
    const bail=new THREE.Mesh(new THREE.CylinderGeometry(.015,.015,.14,6),wood)
    bail.rotation.z=Math.PI/2; bail.position.set(x,.745,0); grp.add(bail)
  })
  grp.position.z=z; scene.add(grp); return grp
}

// ─── Build bowler ─────────────────────────────────────────────────────────────
function buildBowler(scene:THREE.Scene): THREE.Group {
  const grp  = new THREE.Group()
  const skin = new THREE.MeshStandardMaterial({color:0xf4c68d,roughness:.8})
  const kit  = new THREE.MeshStandardMaterial({color:0x1a3a8f,roughness:.8})
  const wkit = new THREE.MeshStandardMaterial({color:0xffffff,roughness:.8})
  // Body
  const body=new THREE.Mesh(new THREE.BoxGeometry(.38,.65,.22),kit); body.position.y=.95; grp.add(body)
  // Head
  const head=new THREE.Mesh(new THREE.SphereGeometry(.16,10,8),skin); head.position.y=1.45; grp.add(head)
  // Helmet
  const helm=new THREE.Mesh(new THREE.SphereGeometry(.18,10,8,0,Math.PI*2,0,Math.PI*.6),
    new THREE.MeshStandardMaterial({color:0x1a1a4a,roughness:.5,metalness:.3}))
  helm.position.y=1.5; grp.add(helm)
  // Legs
  ;[-1,1].forEach(s=>{
    const leg=new THREE.Mesh(new THREE.BoxGeometry(.16,.6,.16),wkit)
    leg.position.set(s*.1,.35,0); grp.add(leg)
  })
  // Arms
  ;[-1,1].forEach(s=>{
    const arm=new THREE.Mesh(new THREE.CylinderGeometry(.06,.06,.45,8),kit)
    arm.rotation.z=s*.6; arm.position.set(s*.3,.95,0); grp.add(arm)
  })
  // Ball hand
  const hand=new THREE.Mesh(new THREE.SphereGeometry(.08,8,8),
    new THREE.MeshStandardMaterial({color:0xcc2222,roughness:.4}))
  hand.position.set(-.45,.75,0); grp.add(hand)

  grp.position.set(0,0,-14); scene.add(grp); return grp
}

// ─── Build batsman ────────────────────────────────────────────────────────────
function buildBatsman(scene:THREE.Scene): THREE.Group {
  const grp = new THREE.Group()
  const skin=new THREE.MeshStandardMaterial({color:0xf4c68d,roughness:.8})
  const kit =new THREE.MeshStandardMaterial({color:0x1a3a8f,roughness:.8})
  const wkit=new THREE.MeshStandardMaterial({color:0xffffff,roughness:.8})
  const batM=new THREE.MeshStandardMaterial({color:0xd4a84b,roughness:.6})
  // Body
  const body=new THREE.Mesh(new THREE.BoxGeometry(.38,.65,.22),kit); body.position.y=.95; grp.add(body)
  // Head + helmet
  const head=new THREE.Mesh(new THREE.SphereGeometry(.16,10,8),skin); head.position.y=1.45; grp.add(head)
  const helm=new THREE.Mesh(new THREE.SphereGeometry(.18,10,8,0,Math.PI*2,0,Math.PI*.6),
    new THREE.MeshStandardMaterial({color:0x4a1a1a,roughness:.5}))
  helm.position.y=1.5; grp.add(helm)
  // Legs
  ;[-1,1].forEach(s=>{
    const leg=new THREE.Mesh(new THREE.BoxGeometry(.16,.6,.16),wkit); leg.position.set(s*.1,.35,0); grp.add(leg)
  })
  // Bat
  const bat=new THREE.Mesh(new THREE.BoxGeometry(.08,.7,.12),batM)
  bat.rotation.z=.4; bat.position.set(.4,.7,0); grp.add(bat)

  grp.position.set(.4,0,6.5); grp.rotation.y=Math.PI; scene.add(grp); return grp
}

// ─── Easing ───────────────────────────────────────────────────────────────────
const easeInOut=(t:number)=>t<.5?2*t*t:1-Math.pow(-2*t+2,2)/2

// ─── Main component ───────────────────────────────────────────────────────────
export function CricketGame({token,balance:initBalance,playerId:initPid,operatorId:initOid}:Props){
  const mountRef  = useRef<HTMLDivElement>(null)
  const socketRef = useRef<Socket|null>(null)
  const sessionRef= useRef({playerId:initPid||'',operatorId:initOid||''})

  const rendRef   = useRef<THREE.WebGLRenderer|null>(null)
  const ballRef   = useRef<THREE.Mesh|null>(null)
  const stumpsRef = useRef<THREE.Group|null>(null)
  const batsmanRef= useRef<THREE.Object3D|null>(null)
  const bowlerRef = useRef<THREE.Object3D|null>(null)
  const rafRef    = useRef(0)
  const camRef    = useRef<THREE.PerspectiveCamera|null>(null)

  const anim = useRef({
    bowling:  false,
    t:        0,
    result:   null as 'six'|'wicket'|null,
    phase:    'idle' as 'idle'|'bowl'|'impact'|'six_fly'|'wicket_fly',
    stumpVel: [] as {mesh:THREE.Object3D,vx:number,vy:number,vz:number,rx:number,rz:number}[],
    batSwing: 0,
    camShake: 0,
  })

  const [phase,setPhase]       = useState<Phase>('idle')
  const [balance,setBalance]   = useState<number|null>(initBalance)
  const [stake,setStake]       = useState(20)
  const [myBet,setMyBet]       = useState<number|null>(null)
  const [sixesHit,setSixesHit] = useState(0)
  const [multiplier,setMultiplier] = useState(1.0)
  const [potential,setPotential]   = useState(0)
  const [toast,setToast]       = useState('')
  const [history,setHistory]   = useState<Array<{sixes:number;mult:number;win:boolean}>>([])
  const [nextWicketChance,setNextWicketChance] = useState(14)
  const [currency,setCurrency] = useState('coins')
  const [modelsLoaded,setModelsLoaded] = useState(false)
  const [loadingMsg,setLoadingMsg] = useState('Loading 3D models...')

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])
  const showToast=(msg:string,d=2500)=>{ setToast(msg); setTimeout(()=>setToast(''),d) }

  // ── Three.js setup ──────────────────────────────────────────────────────────
  useEffect(()=>{
    const el=mountRef.current; if(!el) return
    const W=el.clientWidth, H=el.clientHeight

    const renderer=new THREE.WebGLRenderer({antialias:true})
    renderer.setSize(W,H); renderer.setPixelRatio(Math.min(window.devicePixelRatio,2))
    renderer.shadowMap.enabled=true; renderer.shadowMap.type=THREE.PCFSoftShadowMap
    renderer.setClearColor(0x87ceeb)  // sky blue
    el.appendChild(renderer.domElement); rendRef.current=renderer

    const scene=new THREE.Scene()
    scene.fog=new THREE.Fog(0xaaddff,60,120)

    // Sky gradient dome
    const sky=new THREE.Mesh(
      new THREE.SphereGeometry(100,32,16),
      new THREE.MeshBasicMaterial({color:0x87ceeb,side:THREE.BackSide})
    )
    scene.add(sky)

    // Camera — behind batsman, angled down pitch
    const cam=new THREE.PerspectiveCamera(55,W/H,.1,200)
    cam.position.set(0,3.5,11); cam.lookAt(0,1,-10); camRef.current=cam

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff,1.8))
    const sun=new THREE.DirectionalLight(0xfff5d0,2.5)
    sun.position.set(15,30,10); sun.castShadow=true
    sun.shadow.mapSize.set(2048,2048); scene.add(sun)
    const fill=new THREE.DirectionalLight(0xd0e8ff,1.0)
    fill.position.set(-10,10,-10); scene.add(fill)

    buildStadium(scene)
    // Batsman-end stumps (behind camera, simple)
    buildStumps(scene,8)

    // Load GLB models
    const loader = new GLTFLoader()
    loader.setMeshoptDecoder(MeshoptDecoder)
    const loadM = (url:string):Promise<THREE.Group> => new Promise((res,rej)=>
      loader.load(url,(g)=>res(g.scene),undefined,rej))

    setLoadingMsg('Loading batsman...')
    Promise.all([
      loadM('/models/cricket/batsman.glb'),
      loadM('/models/cricket/bowler.glb'),
      loadM('/models/cricket/ball.glb'),
      loadM('/models/cricket/stumps.glb'),
    ]).then(([batsman,bowler,ballModel,stumps])=>{

      // ── Batsman (1.9m tall, feet at Y=-0.951) ───────────────────────────
      batsman.scale.setScalar(1.0)
      batsman.position.set(0.4, 0.951, 6.5)
      batsman.rotation.y = Math.PI
      batsman.traverse(c=>{ c.castShadow=true })
      batsmanRef.current = batsman; scene.add(batsman)

      // ── Bowler (same scale) ──────────────────────────────────────────────
      bowler.scale.setScalar(1.0)
      bowler.position.set(0, 0.951, -14)
      bowler.traverse(c=>{ c.castShadow=true })
      bowlerRef.current = bowler; scene.add(bowler)

      // ── Ball (scale down from 1.89m → 0.075m) ───────────────────────────
      const ballGroup = new THREE.Group()
      ballModel.scale.setScalar(0.079)
      ballGroup.add(ballModel)
      ballGroup.position.set(-0.45, 0.85, -14)
      ballGroup.visible = false
      ballGroup.traverse(c=>{ c.castShadow=true })
      ballRef.current = ballGroup as any; scene.add(ballGroup)

      // ── Stumps (scale to 0.72m tall) ────────────────────────────────────
      stumps.scale.setScalar(0.38)
      stumps.position.set(0, 0.951*0.38, -14)
      stumps.traverse(c=>{ c.castShadow=true })
      stumpsRef.current = stumps; scene.add(stumps)

      setModelsLoaded(true)
    }).catch(err=>{
      console.error('GLB load error',err)
      setLoadingMsg('Failed to load models')
    })

    const onResize=()=>{
      const w=el.clientWidth,h=el.clientHeight
      renderer.setSize(w,h); cam.aspect=w/h; cam.updateProjectionMatrix()
    }
    window.addEventListener('resize',onResize)

    // ── Render loop ──────────────────────────────────────────────────────────
    const BOWL_START  = new THREE.Vector3(-.45,.85,-13.5)
    const BOWL_CTRL   = new THREE.Vector3(0,1.8,-4)
    const BOWL_END    = new THREE.Vector3(0,.45,5)
    const SIX_END     = new THREE.Vector3((Math.random()-.5)*8, 20, -35)

    const quadBez=(t:number,p0:THREE.Vector3,p1:THREE.Vector3,p2:THREE.Vector3)=>{
      const u=1-t
      return new THREE.Vector3(
        u*u*p0.x+2*u*t*p1.x+t*t*p2.x,
        u*u*p0.y+2*u*t*p1.y+t*t*p2.y,
        u*u*p0.z+2*u*t*p1.z+t*t*p2.z,
      )
    }

    const loop=()=>{
      rafRef.current=requestAnimationFrame(loop)
      const A=anim.current
      const dt=.016

      if(A.phase==='bowl'){
        A.t=Math.min(A.t+dt/1.4,1)
        const pos=quadBez(easeInOut(A.t),BOWL_START,BOWL_CTRL,BOWL_END)
        const ball=ballRef.current; if(ball){ ball.position.copy(pos); ball.rotation.x+=.25 }
        // Bowler arm swing
        const bowler=bowlerRef.current; if(bowler) bowler.rotation.z=-.1+A.t*.3
        if(A.t>=1){
          if(A.result==='six') A.phase='impact'
          else                  A.phase='wicket_fly'
        }
      }
      else if(A.phase==='impact'){
        // Bat swing
        A.batSwing=Math.min(A.batSwing+dt*6,1.2)
        const batsman=batsmanRef.current; if(batsman) batsman.rotation.z=-A.batSwing*.35
        A.t+=dt
        if(A.t>1.3){ A.phase='six_fly'; A.t=0 }
      }
      else if(A.phase==='six_fly'){
        A.t=Math.min(A.t+dt/1.6,1)
        const pos=quadBez(easeInOut(A.t),BOWL_END,
          new THREE.Vector3(SIX_END.x,12,SIX_END.z*.5), SIX_END)
        const ball2=ballRef.current; if(ball2) ball2.position.copy(pos)
        if(A.t>=1){ A.phase='idle'; if(ball2) (ball2 as any).visible=false }
      }
      else if(A.phase==='wicket_fly'){
        A.t+=dt
        // Ball hits stumps
        const ballW=ballRef.current; if(ballW) ballW.position.set(0,.4,-14)
        A.stumpVel.forEach(sv=>{
          sv.mesh.position.x+=sv.vx; sv.mesh.position.y+=sv.vy
          sv.mesh.position.z+=sv.vz; sv.vy-=.008
          sv.mesh.rotation.x+=sv.rx; sv.mesh.rotation.z+=sv.rz
        })
        A.camShake*=.9
        if(camRef.current&&A.camShake>0.01){
          camRef.current.position.x+=(Math.random()-.5)*A.camShake*.06
          camRef.current.position.y+=(Math.random()-.5)*A.camShake*.03
        }
        if(A.t>2){ A.phase='idle'; const bw=ballRef.current; if(bw) (bw as any).visible=false }
      }
      else {
        // Idle: bowler gentle idle sway
        const idleBowler=bowlerRef.current; if(idleBowler) idleBowler.rotation.y=Math.sin(Date.now()*.001)*.05
      }

      renderer.render(scene,cam)
    }
    rafRef.current=requestAnimationFrame(loop)
    return()=>{ window.removeEventListener('resize',onResize); cancelAnimationFrame(rafRef.current); renderer.dispose(); el.removeChild(renderer.domElement) }
  },[])

  // ── Trigger bowling animation ─────────────────────────────────────────────
  const startBowlAnim=useCallback(()=>{
    const A=anim.current; const ball=ballRef.current; const bat=batsmanRef.current
    if(!ball) return
    ball.visible=true
    A.phase='bowl'; A.t=0; A.result=null; A.batSwing=0
    if(bat) bat.children.forEach((c,i)=>{ if(i===5) (c as any).rotation.z=.4 })
  },[])

  const triggerWicket=useCallback(()=>{
    const A=anim.current; const stumps=stumpsRef.current; if(!stumps) return
    // With GLB (single mesh), scatter the whole group
    A.stumpVel=[{
      mesh:stumps,
      vx:(Math.random()-.5)*.1, vy:.3+Math.random()*.1,
      vz:.15+Math.random()*.05,
      rx:.08+Math.random()*.06, rz:(Math.random()-.5)*.08,
    }]
    A.camShake=1; A.phase='wicket_fly'
  },[])

  // ── Socket ────────────────────────────────────────────────────────────────
  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}

    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json(); if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })

    s.on('cricket:bet_placed',(d:any)=>{
      setMyBet(d.stake); setPhase('batting'); setSixesHit(0)
      setMultiplier(1.0); setPotential(d.stake)
      showToast('🏏 Bet placed — Play Ball!',1800)
    })

    s.on('cricket:six',(d:any)=>{
      anim.current.result='six'
      setTimeout(()=>{
        setSixesHit(d.sixes_hit); setMultiplier(d.multiplier)
        setPotential(d.potential); setNextWicketChance(d.next_wicket_chance)
        setPhase('six')
        Sounds.cashout()
        showToast(`🏏 SIX! ${d.multiplier}× — ₹${d.potential.toFixed(2)}`,2000)
      },2000)
    })

    s.on('cricket:wicket',(d:any)=>{
      anim.current.result='wicket'
      setTimeout(()=>{
        triggerWicket()
        setPhase('wicket'); setMyBet(null)
        if(typeof d.balance==='number') setBalance(d.balance)
        setHistory(h=>[{sixes:d.sixes_hit,mult:0,win:false},...h].slice(0,8))
        Sounds.cooked()
        showToast(`💔 WICKET! ${d.sixes_hit} sixes hit — Out!`,2500)
        setTimeout(()=>{ setPhase('idle'); setSixesHit(0) },3000)
      },1900)
    })

    s.on('cricket:retired',(d:any)=>{
      setPhase('retired')
      if(typeof d.balance==='number') setBalance(d.balance)
      setHistory(h=>[{sixes:d.sixes_hit,mult:d.multiplier,win:true},...h].slice(0,8))
      Sounds.cashout()
      showToast(`🏆 RETIRED! ${d.sixes_hit} sixes × ${d.multiplier} = ₹${d.payout.toFixed(2)}!`,3500)
      setTimeout(()=>{ setPhase('idle'); setMyBet(null); setSixesHit(0); setMultiplier(1.0) },3500)
    })

    s.on('cricket:error',(d:any)=>{ showToast(`❌ ${d.error}`) })
    s.on('balance',(d:any)=>{ if(typeof d.balance==='number') setBalance(d.balance) })
    return()=>{ s.disconnect() }
  },[token,triggerWicket])

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleBet=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||myBet) return
    if(balance!==null&&balance<stake) return showToast('Not enough balance')
    setBalance(b=>b!==null?Math.max(0,b-stake):b)
    s.emit('cricket:bet',{player_id:playerId,operator_id:operatorId,stake})
    Sounds.go()
  }

  const handlePlay=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||!myBet||(phase!=='batting'&&phase!=='six')) return
    setPhase('bowling'); startBowlAnim()
    s.emit('cricket:play',{player_id:playerId,operator_id:operatorId})
    Sounds.hop()
  }

  const handleRetire=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||!myBet||sixesHit===0) return
    s.emit('cricket:retire',{player_id:playerId,operator_id:operatorId})
  }

  const STAKES=[5,10,20,50,100,200,500]
  const multColor=multiplier<2?'#fff':multiplier<5?'#4ade80':multiplier<10?'#fbbf24':'#f87171'

  return(
    <div style={{display:'flex',flexDirection:'column',height:'100%',
      background:'#0a1a0a',color:'#fff',fontFamily:'system-ui,sans-serif',
      userSelect:'none',overflow:'hidden',position:'relative'}}>
      <style>{`
        @keyframes cToast{0%{opacity:0;transform:translateX(-50%) scale(.85)}15%,85%{opacity:1;transform:translateX(-50%) scale(1)}100%{opacity:0}}
        @keyframes cPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.05)}}
        @keyframes cSix{0%{transform:translateX(-50%) scale(.5);opacity:0}50%{transform:translateX(-50%) scale(1.3);opacity:1}100%{transform:translateX(-50%) scale(1);opacity:1}}
        .cbtn:hover{filter:brightness(1.15)}.cbtn:active{transform:scale(.95)!important}
      `}</style>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'6px 12px',background:'rgba(0,0,0,.65)',
        borderBottom:'1px solid rgba(74,222,128,.15)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:16}}>🏏</span>
          <strong style={{fontSize:12,letterSpacing:.8,color:'#4ade80'}}>CRICKET SIXES</strong>
          {sixesHit>0&&(
            <span style={{background:'rgba(74,222,128,.2)',border:'1px solid rgba(74,222,128,.4)',
              borderRadius:10,padding:'1px 7px',fontSize:10,fontWeight:700,color:'#4ade80'}}>
              {sixesHit*6} RUNS
            </span>
          )}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <SoundToggleBtn/>
          <div style={{background:'rgba(74,222,128,.1)',border:'1px solid rgba(74,222,128,.3)',
            borderRadius:7,padding:'3px 9px',fontWeight:700,color:'#4ade80',fontSize:12}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:'absolute',top:52,left:'50%',zIndex:60,
          animation:'cToast 2.5s ease-in-out forwards',
          background:'rgba(0,0,0,.92)',color:'#4ade80',padding:'7px 18px',
          borderRadius:20,fontSize:14,fontWeight:800,whiteSpace:'nowrap',
          border:'1px solid rgba(74,222,128,.4)'}}>
          {toast}
        </div>
      )}

      {/* Loading overlay */}
      {!modelsLoaded&&(
        <div style={{position:'absolute',inset:0,zIndex:50,
          background:'rgba(0,0,0,.85)',display:'flex',flexDirection:'column',
          alignItems:'center',justifyContent:'center',gap:12}}>
          <div style={{fontSize:32}}>🏏</div>
          <div style={{fontSize:13,color:'#4ade80',fontWeight:700}}>{loadingMsg}</div>
          <div style={{width:120,height:3,background:'rgba(255,255,255,.1)',borderRadius:2}}>
            <div style={{height:'100%',background:'#4ade80',borderRadius:2,
              animation:'cPulse 1s infinite',width:'60%'}}/>
          </div>
        </div>
      )}
      {/* Three.js canvas */}
      <div ref={mountRef} style={{flex:1,minHeight:0,position:'relative'}}>

        {/* Multiplier overlay */}
        {myBet&&(phase==='six'||phase==='batting'||phase==='bowling')&&(
          <div style={{position:'absolute',top:12,left:'50%',
            transform:'translateX(-50%)',zIndex:10,textAlign:'center',
            animation:phase==='six'?'cSix .5s ease-out':'none'}}>
            <div style={{fontSize:38,fontWeight:900,color:multColor,
              textShadow:`0 0 20px ${multColor}`,lineHeight:1}}>
              {multiplier.toFixed(2)}×
            </div>
            <div style={{fontSize:10,color:'rgba(255,255,255,.5)'}}>
              ₹{potential.toFixed(2)} if retire
            </div>
          </div>
        )}

        {/* Wicket overlay */}
        {phase==='wicket'&&(
          <div style={{position:'absolute',top:'40%',left:'50%',
            transform:'translate(-50%,-50%)',zIndex:10,textAlign:'center',
            background:'rgba(0,0,0,.8)',borderRadius:16,padding:'12px 24px',
            border:'2px solid rgba(239,68,68,.5)'}}>
            <div style={{fontSize:32}}>🚨</div>
            <div style={{fontSize:20,fontWeight:900,color:'#ef4444'}}>WICKET!</div>
            <div style={{fontSize:11,color:'rgba(255,255,255,.5)',marginTop:4}}>{sixesHit} sixes hit</div>
          </div>
        )}

        {/* History */}
        <div style={{position:'absolute',bottom:6,left:'50%',transform:'translateX(-50%)',
          display:'flex',gap:4,zIndex:10}}>
          {history.map((h,i)=>(
            <div key={i} style={{
              borderRadius:8,padding:'2px 6px',fontSize:9,fontWeight:800,
              background:h.win?'rgba(74,222,128,.2)':'rgba(239,68,68,.2)',
              color:h.win?'#4ade80':'#ef4444',
              border:`1px solid ${h.win?'rgba(74,222,128,.3)':'rgba(239,68,68,.3)'}`,
              opacity:1-i*.1,
            }}>
              {h.win?`${h.sixes}🏏${h.mult}×`:`${h.sixes}🏏💔`}
            </div>
          ))}
        </div>

        {/* Wicket danger indicator */}
        {phase==='six'&&(
          <div style={{position:'absolute',bottom:14,right:12,zIndex:10,
            background:'rgba(0,0,0,.7)',borderRadius:8,padding:'4px 10px',
            border:'1px solid rgba(239,68,68,.3)',fontSize:9,color:'rgba(239,68,68,.8)'}}>
            ⚠️ {nextWicketChance}% wicket chance
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{background:'linear-gradient(to top,rgba(0,0,0,.98),rgba(0,0,0,.75))',
        borderTop:'1px solid rgba(74,222,128,.1)',
        padding:'7px 10px 11px',flexShrink:0}}>

        {/* Stake selector — only when idle */}
        {(phase==='idle'||phase==='retired'||phase==='wicket')&&(
          <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:7}}>
            <span style={{fontSize:9,color:'rgba(255,255,255,.3)',whiteSpace:'nowrap'}}>BET:</span>
            <div style={{display:'flex',gap:3,overflowX:'auto',flex:1}}>
              {STAKES.map(sv=>(
                <button key={sv} className="cbtn"
                  onClick={()=>setStake(sv)}
                  style={{
                    borderRadius:5,padding:'3px 7px',cursor:'pointer',flexShrink:0,
                    border:stake===sv?'1.5px solid #4ade80':'1.5px solid rgba(255,255,255,.1)',
                    background:stake===sv?'rgba(74,222,128,.18)':'rgba(255,255,255,.04)',
                    color:stake===sv?'#4ade80':'rgba(255,255,255,.4)',
                    fontSize:10,fontWeight:700,whiteSpace:'nowrap',transition:'all .1s',
                  }}>
                  ₹{sv>=1000?`${sv/1000}K`:sv}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        {(phase==='idle'||phase==='retired'||phase==='wicket')&&(
          <button className="cbtn" onClick={handleBet}
            style={{width:'100%',padding:'11px 0',borderRadius:10,border:'none',
              cursor:'pointer',fontWeight:900,fontSize:15,transition:'all .15s',
              background:'linear-gradient(135deg,#15803d,#166534)',color:'#fff',
              boxShadow:'0 4px 20px rgba(21,128,61,.4)'}}>
            🏏 PLACE BET ₹{stake}
          </button>
        )}

        {(phase==='batting'||phase==='six')&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <button className="cbtn" onClick={handlePlay}
              disabled={false}
              style={{padding:'12px 0',borderRadius:10,border:'none',
                cursor:'pointer',fontWeight:900,fontSize:14,transition:'all .15s',
                background:'linear-gradient(135deg,#1d4ed8,#1e40af)',color:'#fff',
                boxShadow:'0 4px 16px rgba(29,78,216,.4)',
                animation:'cPulse 1s infinite'}}>
              🏏 PLAY BALL
            </button>
            <button className="cbtn" onClick={handleRetire}
              disabled={sixesHit===0}
              style={{padding:'12px 0',borderRadius:10,border:'none',
                cursor:sixesHit>0?'pointer':'default',fontWeight:900,fontSize:13,
                transition:'all .15s',
                background:sixesHit>0
                  ?'linear-gradient(135deg,#d97706,#b45309)':'rgba(255,255,255,.07)',
                color:sixesHit>0?'#fff':'rgba(255,255,255,.2)',
                boxShadow:sixesHit>0?'0 4px 16px rgba(217,119,6,.4)':'none'}}>
              🏆 RETIRE<br/>
              <span style={{fontSize:10,fontWeight:400}}>
                {sixesHit>0?`₹${potential.toFixed(2)}`:'Hit a six first'}
              </span>
            </button>
          </div>
        )}

        {phase==='bowling'&&(
          <div style={{width:'100%',padding:'12px 0',borderRadius:10,textAlign:'center',
            background:'rgba(255,255,255,.05)',color:'rgba(255,255,255,.3)',fontSize:14,fontWeight:700}}>
            ⚾ Ball in flight...
          </div>
        )}
      </div>
    </div>
  )
}
